import { ChangeDetectionStrategy, Component, computed } from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { StateService } from "../../services/state.service";
import { Item } from "../../pojo/Item";
import { S25ItemI } from "../../pojo/S25ItemI";
import { NameService } from "../../services/name.service";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { from, map, Observable, of, switchMap } from "rxjs";
import { S25RouterLinkDirective } from "../s25-nav-bar/s25.router.link.directive";
import { S25IconComponent } from "../s25-icon/s25.icon.component";
import { S25Icons } from "../s25-icon/s25.icon.const";
import { S25States } from "../../services/state/state.map";
declare const angular: any;

type RouteHistory = {
    from: { state: { name: S25States.Name }; paramValues: S25ItemI };
    to: { _identifier: S25States.Name; _params: S25ItemI };
};

@TypeManagerDecorator("se-breadcrumbs")
@Component({
    selector: "se-breadcrumbs",
    template: `
        <a s25-ng-router-link [stateName]="'studentEngagementAdmin'" [stateParams]="{ mode: 'dash' }">
            <s25-ng-icon [type]="'home'"></s25-ng-icon>
            Home
        </a>
        @if (routeHistory().length) {
            @for (route of routeHistory(); track route) {
                @let objData = objIdMap[route.to._identifier];
                @let nameKey = route.to._params.itemId + "-" + objData?.itemTypeId;
                <a
                    s25-ng-router-link
                    [stateName]="route.to._identifier"
                    [stateParams]="{ itemId: +route.to._params.itemId! }"
                >
                    <s25-ng-icon [type]="objData?.iconProp"></s25-ng-icon>
                    {{ nameMap()![nameKey] }}
                </a>

                @if (!$last) {
                    <s25-ng-icon [type]="'caretRight'"></s25-ng-icon>
                }
            }
        }
    `,
    host: {
        class: "c-margin-bottom--half",
    },
    styles: `
        :host {
            --link-color: #74818c;
            display: flex;
            align-items: center;
            gap: 1em;
            color: var(--link-color);

            a {
                display: flex;
                align-items: center;
                gap: 5px;
                color: var(--link-color);
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25RouterLinkDirective, S25IconComponent],
})
export class SeBreadcrumbComponent {
    private transitionSuccess$ = new Observable((subscriber) => {
        const $rootScope = angular.element(document.body).injector().get("$rootScope");
        const deregisterFn = $rootScope.$on("$stickyTransitionSuccess", () => subscriber.next());
        return () => deregisterFn();
    });

    protected routeHistory = toSignal(
        this.transitionSuccess$.pipe(
            map((): RouteHistory[] => {
                const routes =
                        StateService.getRouteHistory()?.filter(
                            (route: RouteHistory) => route.to._identifier in this.objIdMap, // object type, not home
                        ) ?? [],
                    seen = new Set<string>(),
                    mostRecentRoutes = routes.slice(-5);
                return mostRecentRoutes
                    .reverse() // filter in reverse to keep most recent state
                    .filter((route: RouteHistory) => {
                        // no duplicates
                        const key = `${route.to._identifier}-${route.to._params.itemId}`;
                        if (seen.has(key)) return false;
                        else {
                            seen.add(key);
                            return true;
                        }
                    })
                    .reverse();
            }),
        ),
        { initialValue: [] as RouteHistory[] },
    );

    objIdMap: Record<string, { itemTypeId: Item.Ids; iconProp: S25Icons.Name }> = {
        studentEngagementEvent: { itemTypeId: Item.Ids.Event, iconProp: S25Icons.names.calendarDate },
        studentEngagementOrganization: { itemTypeId: Item.Ids.Organization, iconProp: S25Icons.names.organization },
    };

    private objects = computed(() =>
        this.routeHistory().map((route: RouteHistory) => ({
            itemId: +route.to._params.itemId!,
            itemTypeId: this.objIdMap[route.to._identifier]?.itemTypeId,
        })),
    );

    protected nameMap = toSignal<Record<string, string>>(
        toObservable(this.objects).pipe(
            switchMap((objects) => (objects.length ? from(NameService.getNames(objects)) : of({}))),
        ),
    );
}
